import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MapPin, Truck, Package, Search, Clock, Calendar, AlertTriangle, Home, Shield, Briefcase, WifiOff } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import { CustomSelect } from '../common/CustomSelect';
import api from '../../api/auth.api';
import { API_CONFIG } from '../../config/api.config';

// Offline storage keys
const STORAGE_KEYS = {
  CLOCK_STATUS: 'cdl_clock_status',
  CDL_STATUS: 'cdl_status',
  JOBS: 'cdl_jobs',
  EQUIPMENT: 'cdl_equipment',
  PENDING_ACTIONS: 'cdl_pending_actions'
};

interface PendingAction {
  id: string;
  type: 'load' | 'unload';
  equipment_id?: number;
  equipment_name: string;
  job_id: number | null;
  job_name: string;
  lat?: number;
  lng?: number;
  is_rental?: boolean;
  rental_company?: string;
  rental_description?: string;
  timestamp: number;
}

// Pending actions queue helpers
const getPendingActions = (employeeId: number): PendingAction[] => {
  try {
    const cached = localStorage.getItem(`${STORAGE_KEYS.PENDING_ACTIONS}_${employeeId}`);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
};

const addPendingAction = (employeeId: number, action: PendingAction) => {
  const actions = getPendingActions(employeeId);
  actions.push(action);
  localStorage.setItem(`${STORAGE_KEYS.PENDING_ACTIONS}_${employeeId}`, JSON.stringify(actions));
};

const removePendingAction = (employeeId: number, actionId: string) => {
  const actions = getPendingActions(employeeId).filter(a => a.id !== actionId);
  localStorage.setItem(`${STORAGE_KEYS.PENDING_ACTIONS}_${employeeId}`, JSON.stringify(actions));
};

const clearPendingActions = (employeeId: number) => {
  localStorage.removeItem(`${STORAGE_KEYS.PENDING_ACTIONS}_${employeeId}`);
};

// Cache helper functions
const saveToCache = (key: string, data: any, employeeId: number) => {
  try {
    localStorage.setItem(`${key}_${employeeId}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('Error saving to cache:', e);
  }
};

const getFromCache = (key: string, employeeId: number, maxAgeHours: number = 24) => {
  try {
    const cached = localStorage.getItem(`${key}_${employeeId}`);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);

    if (ageHours > maxAgeHours) {
      localStorage.removeItem(`${key}_${employeeId}`);
      return null;
    }

    return data;
  } catch (e) {
    return null;
  }
};

interface JobWithGeofence {
  job_id: number;
  job_name: string;
  job_number: string;
  lat: number;
  lng: number;
  geofence_type?: 'circle' | 'polygon';
  geofence_data?: {
    radius?: number;
    points?: { lat: number; lng: number }[];
  };
}

interface Equipment {
  equipment_id: number;
  bradanna_equipment_number: string;
  equipment_name: string;
  equipment_type: string;
  serial_number?: string;
  current_job_id: number | null;
  job_name?: string;
  bradanna_job_number?: string;
  status?: string;
}

interface LoadedEquipment {
  movement_id: number;
  equipment_id: number;
  loaded_at: string;
  is_rental: boolean;
  rental_company?: string;
  rental_equipment_type?: string;
  rental_description?: string;
  bradanna_equipment_number?: string;
  equipment_name?: string;
  equipment_type?: string;
  from_job_name?: string;
}

interface DailyLogEntry {
  entry_id: number;
  job_name: string;
  bradanna_job_number?: string;
  clock_in: string;
  clock_out?: string;
  hours_worked?: number;
  picked_up?: string[];
  dropped_off?: string[];
  dump_loads?: number;
  dump_materials?: string[];
  activities?: { activity_type: string; description: string }[];
}

// Shop location for CDEL truck driver
const SHOP_LOCATION = {
  name: 'Main Shop',
  lat: 26.171185,
  lng: -81.677528,
  radius: 300 // meters
};

// Geofence helper functions
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371e3;
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const isInsideCircle = (userLat: number, userLng: number, centerLat: number, centerLng: number, radius: number): boolean => {
  return calculateDistance(userLat, userLng, centerLat, centerLng) <= radius;
};

const isInsidePolygon = (userLat: number, userLng: number, points: Array<{ lat: number; lng: number }>): boolean => {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const xi = points[i].lat, yi = points[i].lng;
    const xj = points[j].lat, yj = points[j].lng;
    const intersect = ((yi > userLng) !== (yj > userLng))
        && (userLat < (xj - xi) * (userLng - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
};

const isWithinJobGeofence = (job: JobWithGeofence, userLat: number, userLng: number): boolean => {
  if (job.geofence_type === 'circle' && job.geofence_data?.radius) {
    return isInsideCircle(userLat, userLng, job.lat, job.lng, job.geofence_data.radius);
  } else if (job.geofence_type === 'polygon' && job.geofence_data?.points) {
    return isInsidePolygon(userLat, userLng, job.geofence_data.points);
  }
  return calculateDistance(userLat, userLng, job.lat, job.lng) <= 100;
};

// Check if user is at the shop
const isAtShop = (userLat: number, userLng: number): boolean => {
  return calculateDistance(userLat, userLng, SHOP_LOCATION.lat, SHOP_LOCATION.lng) <= SHOP_LOCATION.radius;
};

// Get distance from shop in miles (for display)
const getDistanceFromShopMiles = (userLat: number, userLng: number): number => {
  const meters = calculateDistance(userLat, userLng, SHOP_LOCATION.lat, SHOP_LOCATION.lng);
  return meters / 1609.34; // Convert meters to miles
};

const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

export const TruckDriverDashboard: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const { ensureFreshLocation, forceRefreshLocation, location, getLocationAge } = useLocation();

  // Core state
  const [initializing, setInitializing] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [usingCachedData, setUsingCachedData] = useState(false);

  // Clock state
  const [clockedIn, setClockedIn] = useState<boolean | null>(null);
  const [activeTimeEntry, setActiveTimeEntry] = useState<any | null>(null);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Refresh data when coming back online
      if (auth?.user?.employee_id) {
        refreshDashboardData();
      }
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [auth?.user?.employee_id]);

  // Job state
  const [jobs, setJobs] = useState<JobWithGeofence[]>([]);
  const [detectedJob, setDetectedJob] = useState<JobWithGeofence | null>(null);
  const [showJobPicker, setShowJobPicker] = useState(false);

  // Equipment state
  const [mode, setMode] = useState<'equipment' | 'dump'>('equipment');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Equipment[]>([]);
  const [searching, setSearching] = useState(false);
  const [loadedEquipment, setLoadedEquipment] = useState<LoadedEquipment[]>([]);
  const [cachedEquipment, setCachedEquipment] = useState<Equipment[]>([]);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);

  // Dump truck state
  const [selectedMaterial, setSelectedMaterial] = useState<string>('fill');

  // iPad detection for larger fonts
  const [isIPad, setIsIPad] = useState(false);

  useEffect(() => {
    const checkIPad = () => {
      const ua = navigator.userAgent;
      const isIPadOS = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      setIsIPad(isIPadOS);
    };
    checkIPad();
  }, []);

  // Font size helper - larger on iPad
  const fontSize = (base: number) => isIPad ? `${base * 1.25}px` : `${base}px`;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load daily log - defined early so it can be used in refreshDashboardData
  // Stores the raw activityLog from backend (consolidated by job location)
  const loadDailyLog = useCallback(async () => {
    try {
      const response = await api.get('/api/cdl/log/daily');
      if (response.data.success) {
        const data = response.data.data;
        // Store the consolidated activity log directly (one entry per job location)
        // Each entry has: job_name, clock_in, clock_out, hours_worked, picked_up[], dropped_off[], dump_loads, dump_materials[], activities[]
        setDailyLog(data.activityLog || []);
      }
    } catch (error) {
      console.error('Error loading daily log:', error);
    }
  }, []);

  // Refresh data function - used when returning to app
  const refreshDashboardData = useCallback(async () => {
    const employeeId = auth?.user?.employee_id;
    if (!employeeId) return;

    console.log('[TruckDriver] Refreshing dashboard data on focus...');
    try {
      // Reload clock status and CDL status
      const [clockStatusResponse, cdlStatusResponse] = await Promise.all([
        api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`),
        api.get('/api/cdl/status')
      ]);

      // Update clock status and cache it
      const isClocked = clockStatusResponse.data.success && clockStatusResponse.data.data?.job_id;
      const timeEntry = isClocked ? clockStatusResponse.data.data : null;
      setClockedIn(isClocked);
      setActiveTimeEntry(timeEntry);
      saveToCache(STORAGE_KEYS.CLOCK_STATUS, { clockedIn: isClocked, activeTimeEntry: timeEntry }, employeeId);
      setUsingCachedData(false);

      // Update CDL status and cache it
      if (cdlStatusResponse.data.success) {
        const cdlData = cdlStatusResponse.data.data;
        setLoadedEquipment(cdlData.loadedEquipment || []);
        setTodaySummary(cdlData.summary);
        saveToCache(STORAGE_KEYS.CDL_STATUS, cdlData, employeeId);
      }

      // Reload daily log
      await loadDailyLog();
    } catch (error) {
      console.error('[TruckDriver] Error refreshing dashboard:', error);
      // If refresh fails and we're offline, don't clear the cached data
    }
  }, [auth?.user?.employee_id, loadDailyLog]);

  // Refresh on window focus (when returning to app from another device update)
  useEffect(() => {
    const handleFocus = () => {
      window.scrollTo(0, 0);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        window.scrollTo(0, 0);
        // Refresh data when app becomes visible (user returns to app)
        refreshDashboardData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshDashboardData]);

  // Scroll to top when initialization completes
  useEffect(() => {
    if (!initializing) {
      window.scrollTo(0, 0);
    }
  }, [initializing]);

  // Daily log state
  const [dailyLog, setDailyLog] = useState<DailyLogEntry[]>([]);
  const [todaySummary, setTodaySummary] = useState<any>(null);

  // Modal state
  const [showEndDayConfirm, setShowEndDayConfirm] = useState(false);
  const [showDailyLogModal, setShowDailyLogModal] = useState(false);
  const [showWeeklyModal, setShowWeeklyModal] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [showMiscModal, setShowMiscModal] = useState(false);
  const [showOfficerModal, setShowOfficerModal] = useState(false);

  // Clock out result state - shows distance from shop when clocked out at unknown location
  const [clockOutResult, setClockOutResult] = useState<{
    locationName: string;
    distanceFromShop?: number; // in miles
  } | null>(null);

  // Rental form state
  const [rentalCompany, setRentalCompany] = useState('');
  const [rentalDescription, setRentalDescription] = useState('');

  // Misc form state
  const [miscText, setMiscText] = useState('');
  const [weeklyData, setWeeklyData] = useState<any>(null);

  // Detect job from GPS location
  const detectJobFromLocation = useCallback((lat: number, lng: number, jobList: JobWithGeofence[]) => {
    // First check for jobs within geofence
    for (const job of jobList) {
      if (isWithinJobGeofence(job, lat, lng)) {
        return job;
      }
    }
    // If no geofence match, find nearest job within 500m
    let nearestJob: JobWithGeofence | null = null;
    let nearestDistance = Infinity;
    for (const job of jobList) {
      if (job.lat && job.lng) {
        const distance = calculateDistance(lat, lng, job.lat, job.lng);
        if (distance < nearestDistance && distance <= 500) {
          nearestDistance = distance;
          nearestJob = job;
        }
      }
    }
    return nearestJob;
  }, []);

  // Load data from cache (fallback for offline)
  const loadFromCache = useCallback((employeeId: number) => {
    console.log('[TruckDriver] Loading from cache (offline mode)...');
    setUsingCachedData(true);

    // Load cached clock status
    const cachedClockStatus = getFromCache(STORAGE_KEYS.CLOCK_STATUS, employeeId);
    if (cachedClockStatus) {
      setClockedIn(cachedClockStatus.clockedIn);
      setActiveTimeEntry(cachedClockStatus.activeTimeEntry);
      console.log('[TruckDriver] Restored clock status from cache:', cachedClockStatus.clockedIn ? 'clocked in' : 'not clocked in');
    }

    // Load cached CDL status
    const cachedCdlStatus = getFromCache(STORAGE_KEYS.CDL_STATUS, employeeId);
    if (cachedCdlStatus) {
      setLoadedEquipment(cachedCdlStatus.loadedEquipment || []);
      setTodaySummary(cachedCdlStatus.summary);
    }

    // Load cached jobs
    const cachedJobs = getFromCache(STORAGE_KEYS.JOBS, employeeId);
    if (cachedJobs) {
      setJobs(cachedJobs);
      // Try to detect current job from cached location
      if (location?.latitude && location?.longitude) {
        const detected = detectJobFromLocation(location.latitude, location.longitude, cachedJobs);
        setDetectedJob(detected);
      }
    }
  }, [location, detectJobFromLocation]);

  // Initialize dashboard
  useEffect(() => {
    const initializeDashboard = async () => {
      const employeeId = auth?.user?.employee_id;
      if (!employeeId) return;

      try {
        const startTime = Date.now();
        const MIN_LOADING_TIME = 800;
        setInitializing(true);
        setUsingCachedData(false);

        // Check if location needs refresh (> 5 minutes old)
        const locationAge = getLocationAge();
        const FIVE_MINUTES = 5 * 60 * 1000;
        let currentLocation = location;

        if (!locationAge || locationAge > FIVE_MINUTES) {
          console.log('[TruckDriver] Location stale on page load, refreshing GPS...');
          const freshLoc = await forceRefreshLocation();
          if (freshLoc) {
            currentLocation = freshLoc;
          }
        }

        // Try to load from API
        try {
          // Load clock status, CDL status, and jobs in parallel
          const [clockStatusResponse, cdlStatusResponse, jobsResponse] = await Promise.all([
            api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`),
            api.get('/api/cdl/status'),
            api.get(`${API_CONFIG.ENDPOINTS.JOBS}?is_active=true`)
          ]);

          // Check clock status and cache it
          const isClocked = clockStatusResponse.data.success && clockStatusResponse.data.data?.job_id;
          const timeEntry = isClocked ? clockStatusResponse.data.data : null;
          setClockedIn(isClocked);
          setActiveTimeEntry(timeEntry);
          saveToCache(STORAGE_KEYS.CLOCK_STATUS, { clockedIn: isClocked, activeTimeEntry: timeEntry }, employeeId);

          // Get CDL status (loaded equipment, summary) and cache it
          if (cdlStatusResponse.data.success) {
            const cdlData = cdlStatusResponse.data.data;
            setLoadedEquipment(cdlData.loadedEquipment || []);
            setTodaySummary(cdlData.summary);
            saveToCache(STORAGE_KEYS.CDL_STATUS, cdlData, employeeId);
          }

          // Store jobs for geofence detection and cache them
          if (jobsResponse.data.success && jobsResponse.data.data) {
            const jobList = jobsResponse.data.data.map((job: any) => ({
              job_id: job.job_id,
              job_name: job.job_name,
              job_number: job.bradanna_job_number || '',
              lat: job.lat,
              lng: job.lng,
              geofence_type: job.geofence_type,
              geofence_data: job.geofence_data
            }));
            setJobs(jobList);
            saveToCache(STORAGE_KEYS.JOBS, jobList, employeeId);

            // Try to detect current job from location
            if (currentLocation?.latitude && currentLocation?.longitude) {
              const detected = detectJobFromLocation(currentLocation.latitude, currentLocation.longitude, jobList);
              setDetectedJob(detected);
            }
          }

          // Load today's daily log
          await loadDailyLog();

        } catch (apiError) {
          // API failed - try to load from cache
          console.log('[TruckDriver] API request failed, falling back to cache:', apiError);
          loadFromCache(employeeId);
        }

        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
        if (remainingTime > 0) {
          await new Promise(resolve => setTimeout(resolve, remainingTime));
        }

        setInitializing(false);
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        // Try cache as last resort
        if (auth?.user?.employee_id) {
          loadFromCache(auth.user.employee_id);
        }
        setInitializing(false);
      }
    };

    if (auth?.user?.employee_id) {
      initializeDashboard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.employee_id]);

  // Load pending actions and fetch all equipment on mount (once per day)
  useEffect(() => {
    if (auth?.user?.employee_id) {
      const pending = getPendingActions(auth.user.employee_id);
      setPendingActions(pending);

      // Check if we have recent cached equipment (less than 24 hours old)
      const cacheKey = `${STORAGE_KEYS.EQUIPMENT}_${auth.user.employee_id}`;
      const cachedData = localStorage.getItem(cacheKey);
      let needsFetch = true;

      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const ageHours = (Date.now() - timestamp) / (1000 * 60 * 60);
          setCachedEquipment(data);

          // Only fetch if cache is older than 24 hours
          if (ageHours < 24) {
            needsFetch = false;
            console.log(`[TruckDriver] Using cached equipment (${Math.round(ageHours)}h old, ${data.length} items)`);
          }
        } catch (e) {
          console.error('[TruckDriver] Error parsing cached equipment:', e);
        }
      }

      // Fetch all equipment if online and cache is stale or missing
      if (navigator.onLine && needsFetch) {
        console.log('[TruckDriver] Fetching fresh equipment list...');
        api.get('/api/equipment')
          .then(response => {
            if (response.data.success && response.data.data) {
              const allEquipment = response.data.data.map((eq: any) => ({
                equipment_id: eq.equipment_id,
                equipment_name: eq.equipment_name,
                bradanna_equipment_number: eq.bradanna_equipment_number,
                equipment_type: eq.equipment_type,
                serial_number: eq.serial_number,
                current_job_id: eq.current_job_id,
                job_name: eq.current_job_name,
                status: eq.status
              }));
              setCachedEquipment(allEquipment);
              saveToCache(STORAGE_KEYS.EQUIPMENT, allEquipment, auth.user.employee_id);
              console.log(`[TruckDriver] Cached ${allEquipment.length} equipment items (refreshed daily)`);
            }
          })
          .catch(err => console.error('[TruckDriver] Failed to fetch equipment:', err));
      }
    }
  }, [auth?.user?.employee_id]);

  // Sync pending actions when online
  useEffect(() => {
    const syncPendingActions = async () => {
      if (!isOnline || !auth?.user?.employee_id || pendingActions.length === 0) return;

      console.log('[TruckDriver] Syncing pending actions...', pendingActions.length);

      for (const action of pendingActions) {
        try {
          await api.post('/api/cdl/equipment/movement', {
            equipment_id: action.equipment_id,
            action: action.type,
            job_id: action.job_id,
            lat: action.lat,
            lng: action.lng,
            is_rental: action.is_rental,
            rental_company: action.rental_company,
            rental_description: action.rental_description
          });
          // Remove synced action
          removePendingAction(auth.user.employee_id, action.id);
          console.log('[TruckDriver] Synced action:', action.id);
        } catch (error) {
          console.error('[TruckDriver] Failed to sync action:', action.id, error);
          // Keep in queue to retry later
        }
      }

      // Reload pending actions state
      setPendingActions(getPendingActions(auth.user.employee_id));

      // Refresh dashboard data after sync
      await refreshDashboardData();
    };

    syncPendingActions();
  }, [isOnline, auth?.user?.employee_id]);

  // Search equipment - always search from cached equipment (all equipment loaded on init)
  useEffect(() => {
    const searchEquipment = () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);

      // Search from cached equipment (full list loaded on init)
      const query = searchQuery.toLowerCase();
      const results = cachedEquipment.filter(eq =>
        eq.equipment_name?.toLowerCase().includes(query) ||
        eq.bradanna_equipment_number?.toLowerCase().includes(query) ||
        eq.equipment_type?.toLowerCase().includes(query) ||
        eq.serial_number?.toLowerCase().includes(query)
      );
      setSearchResults(results.slice(0, 20)); // Limit results
      setSearching(false);
    };

    const debounce = setTimeout(searchEquipment, 200);
    return () => clearTimeout(debounce);
  }, [searchQuery, cachedEquipment]);

  // Handle clock in
  const handleClockIn = async (job: JobWithGeofence) => {
    setProcessing(true);
    setError(null);
    setShowJobPicker(false);

    try {
      // Force fresh GPS check before clock-in to verify driver location
      const freshLocation = await ensureFreshLocation();
      const lat = freshLocation?.latitude || location?.latitude;
      const lng = freshLocation?.longitude || location?.longitude;

      if (!lat || !lng) {
        setError('Location is required to clock in. Please enable location services and try again.');
        setProcessing(false);
        return;
      }

      const response = await api.post(`${API_CONFIG.ENDPOINTS.TIME}/clock-in`, {
        job_id: job.job_id,
        latitude: lat,
        longitude: lng
      });

      if (response.data.success) {
        setClockedIn(true);
        setActiveTimeEntry(response.data.data);
        setDetectedJob(job);

        // Reload CDL status to get any equipment still on trailer from previous day
        const cdlStatusResponse = await api.get('/api/cdl/status');
        if (cdlStatusResponse.data.success) {
          setLoadedEquipment(cdlStatusResponse.data.data.loadedEquipment || []);
          setTodaySummary(cdlStatusResponse.data.data.summary);
        }

        await loadDailyLog();
      } else {
        setError(response.data.error || 'Failed to clock in');
      }
    } catch (error: any) {
      console.error('Clock in error:', error);
      setError(error.response?.data?.error || 'Failed to clock in');
    } finally {
      setProcessing(false);
    }
  };

  // Handle start day button click
  const handleStartDay = async () => {
    setProcessing(true);
    setError(null);

    try {
      const freshLocation = await ensureFreshLocation();
      const lat = freshLocation?.latitude || location?.latitude;
      const lng = freshLocation?.longitude || location?.longitude;

      // Location is required to clock in
      if (!lat || !lng) {
        setError('Location is required to clock in. Please enable location services and try again.');
        setProcessing(false);
        return;
      }

      const detected = detectJobFromLocation(lat, lng, jobs);
      if (detected) {
        await handleClockIn(detected);
        return;
      }

      // No job detected - use "Unknown" job as fallback (GPS coords will be logged)
      const unknownJob = jobs.find(j => j.job_name.toLowerCase() === 'unknown');
      if (unknownJob) {
        console.log('📍 No job detected, using Unknown job for CDL trucker clock-in');
        await handleClockIn(unknownJob);
        return;
      }

      // No Unknown job exists - show job picker as last resort
      setProcessing(false);
      setShowJobPicker(true);

    } catch (error) {
      console.error('Location error:', error);
      setError('Could not get your location. Please enable location services and try again.');
      setProcessing(false);
    }
  };

  // Handle end day
  const handleEndDay = async () => {
    setProcessing(true);
    setError(null);
    setShowEndDayConfirm(false);
    setClockOutResult(null);

    try {
      const freshLocation = await ensureFreshLocation();
      const lat = freshLocation?.latitude || location?.latitude;
      const lng = freshLocation?.longitude || location?.longitude;

      // Determine clock out location status
      let locationName = 'Unknown';
      let distanceFromShop: number | undefined = undefined;
      let atShop = false;

      if (lat && lng) {
        // Check if at shop
        if (isAtShop(lat, lng)) {
          locationName = SHOP_LOCATION.name;
          atShop = true;
        } else {
          // Check if at any job geofence
          const detectedJobSite = detectJobFromLocation(lat, lng, jobs);
          if (detectedJobSite) {
            locationName = detectedJobSite.job_name;
          } else {
            // Not at shop or any job - clock out as "Unknown" and calculate distance from shop
            locationName = 'Unknown';
            distanceFromShop = getDistanceFromShopMiles(lat, lng);
          }
        }
      }

      const response = await api.post(`${API_CONFIG.ENDPOINTS.TIME}/clock-out`, {
        latitude: lat,
        longitude: lng,
        clocked_out_at_shop: atShop
      });

      if (response.data.success) {
        setClockedIn(false);
        setActiveTimeEntry(null);
        setLoadedEquipment([]);
        await loadDailyLog();

        // Set clock out result to show location info
        setClockOutResult({
          locationName,
          distanceFromShop
        });
      } else {
        setError(response.data.error || 'Failed to end day');
      }
    } catch (error: any) {
      console.error('End day error:', error);
      setError(error.response?.data?.error || 'Failed to end day');
    } finally {
      setProcessing(false);
    }
  };

  // Handle equipment pick up
  const handlePickUp = async (equipment: Equipment) => {
    setProcessing(true);
    setError(null);

    try {
      // Always force a fresh GPS location for pickups to ensure correct job detection
      console.log('[TruckDriver] Forcing GPS refresh for pickup...');
      const freshLocation = await forceRefreshLocation();

      const lat = freshLocation?.latitude || location?.latitude;
      const lng = freshLocation?.longitude || location?.longitude;

      // Always detect job from fresh GPS - don't use cached detectedJob
      let currentJobId: number | null = null;
      let currentJobName = 'Unknown';
      if (lat && lng) {
        const detected = detectJobFromLocation(lat, lng, jobs);
        if (detected) {
          setDetectedJob(detected);
          currentJobId = detected.job_id;
          currentJobName = detected.job_name;
        }
      }

      // If no job detected, use "Unknown" job as fallback (GPS coords will be logged)
      if (!currentJobId) {
        const unknownJob = jobs.find(j => j.job_name.toLowerCase() === 'unknown');
        if (unknownJob) {
          currentJobId = unknownJob.job_id;
          currentJobName = unknownJob.job_name;
          setDetectedJob(unknownJob);
        } else if (!isOnline) {
          // Offline with no unknown job - still allow pickup
          currentJobName = 'Unknown (Offline)';
        } else {
          // No Unknown job exists - show job picker
          setError('Could not detect job site. Please select a job.');
          setShowJobPicker(true);
          setProcessing(false);
          return;
        }
      }

      // If offline, queue the action for later
      if (!isOnline) {
        const pendingAction: PendingAction = {
          id: `load_${equipment.equipment_id}_${Date.now()}`,
          type: 'load',
          equipment_id: equipment.equipment_id,
          equipment_name: equipment.equipment_name || equipment.bradanna_equipment_number || 'Unknown',
          job_id: currentJobId,
          job_name: currentJobName,
          lat,
          lng,
          timestamp: Date.now()
        };

        if (auth?.user?.employee_id) {
          addPendingAction(auth.user.employee_id, pendingAction);
          setPendingActions(getPendingActions(auth.user.employee_id));

          // Optimistically add to loaded equipment
          const newLoaded: LoadedEquipment = {
            movement_id: -Date.now(), // Temporary negative ID
            equipment_id: equipment.equipment_id,
            loaded_at: new Date().toISOString(),
            is_rental: false,
            bradanna_equipment_number: equipment.bradanna_equipment_number,
            equipment_name: equipment.equipment_name,
            equipment_type: equipment.equipment_type,
            from_job_name: currentJobName
          };
          setLoadedEquipment(prev => [...prev, newLoaded]);
        }

        setSearchQuery('');
        setSearchResults([]);
        setProcessing(false);
        return;
      }

      const response = await api.post('/api/cdl/equipment/movement', {
        equipment_id: equipment.equipment_id,
        action: 'load',
        job_id: currentJobId,
        lat,
        lng
      });

      if (response.data.success) {
        // Refresh loaded equipment and log
        const statusResponse = await api.get('/api/cdl/status');
        if (statusResponse.data.success) {
          setLoadedEquipment(statusResponse.data.data.loadedEquipment || []);
          setTodaySummary(statusResponse.data.data.summary);
        }
        await loadDailyLog();
        setSearchQuery('');
        setSearchResults([]);
      } else {
        setError(response.data.error || 'Failed to log pick up');
      }
    } catch (error: any) {
      console.error('Pick up error:', error);
      setError(error.response?.data?.error || 'Failed to log pick up');
    } finally {
      setProcessing(false);
    }
  };

  // Handle rental equipment pick up
  const handleRentalPickUp = async () => {
    if (!rentalDescription.trim()) {
      setError('Please enter a description for the rental');
      return;
    }

    setProcessing(true);
    setError(null);
    setShowRentalModal(false);

    try {
      // Always force a fresh GPS location for rental pickups
      console.log('[TruckDriver] Forcing GPS refresh for rental pickup...');
      const freshLocation = await forceRefreshLocation();

      const lat = freshLocation?.latitude;
      const lng = freshLocation?.longitude;

      // Always detect job from fresh GPS - don't use cached detectedJob
      let currentJobId = null;
      if (lat && lng) {
        const detected = detectJobFromLocation(lat, lng, jobs);
        if (detected) {
          setDetectedJob(detected);
          currentJobId = detected.job_id;
        }
      }

      // If no job detected, use "Unknown" job as fallback (GPS coords will be logged)
      if (!currentJobId) {
        const unknownJob = jobs.find(j => j.job_name.toLowerCase() === 'unknown');
        if (unknownJob) {
          currentJobId = unknownJob.job_id;
          setDetectedJob(unknownJob);
        } else {
          setError('Could not detect job site. Please select a job.');
          setShowJobPicker(true);
          setProcessing(false);
          return;
        }
      }

      const response = await api.post('/api/cdl/equipment/movement', {
        action: 'load',
        job_id: currentJobId,
        lat,
        lng,
        is_rental: true,
        rental_company: rentalCompany.trim() || 'Unknown',
        rental_description: rentalDescription.trim()
      });

      if (response.data.success) {
        const statusResponse = await api.get('/api/cdl/status');
        if (statusResponse.data.success) {
          setLoadedEquipment(statusResponse.data.data.loadedEquipment || []);
          setTodaySummary(statusResponse.data.data.summary);
        }
        await loadDailyLog();
        setRentalCompany('');
        setRentalDescription('');
      } else {
        setError(response.data.error || 'Failed to log rental pick up');
      }
    } catch (error: any) {
      console.error('Rental pick up error:', error);
      setError(error.response?.data?.error || 'Failed to log rental pick up');
    } finally {
      setProcessing(false);
    }
  };

  // Handle misc item pickup (treated like rental to show on trailer)
  const handleMiscSubmit = async () => {
    if (!miscText.trim()) {
      setError('Please enter a description');
      return;
    }

    setProcessing(true);
    setError(null);
    setShowMiscModal(false);

    try {
      // Always force a fresh GPS location for misc pickups
      console.log('[TruckDriver] Forcing GPS refresh for misc pickup...');
      const freshLocation = await forceRefreshLocation();
      const lat = freshLocation?.latitude;
      const lng = freshLocation?.longitude;

      // Always detect job from fresh GPS - don't use cached detectedJob
      let currentJobId = null;
      if (lat && lng) {
        const detected = detectJobFromLocation(lat, lng, jobs);
        if (detected) {
          setDetectedJob(detected);
          currentJobId = detected.job_id;
        }
      }

      // If no job detected, use "Unknown" job as fallback (GPS coords will be logged)
      if (!currentJobId) {
        const unknownJob = jobs.find(j => j.job_name.toLowerCase() === 'unknown');
        if (unknownJob) {
          currentJobId = unknownJob.job_id;
          setDetectedJob(unknownJob);
        } else {
          setError('Could not detect job site. Please select a job.');
          setShowJobPicker(true);
          setProcessing(false);
          return;
        }
      }

      // Use equipment movement API with is_rental=true and MISC marker
      // This makes misc items show on the trailer and allows drop off
      const response = await api.post('/api/cdl/equipment/movement', {
        action: 'load',
        job_id: currentJobId,
        lat,
        lng,
        is_rental: true,
        rental_company: 'MISC',  // Special marker for misc items
        rental_equipment_type: 'Parts/Materials',
        rental_description: miscText.trim()
      });

      if (response.data.success) {
        // Refresh loaded equipment and log
        const statusResponse = await api.get('/api/cdl/status');
        if (statusResponse.data.success) {
          setLoadedEquipment(statusResponse.data.data.loadedEquipment || []);
          setTodaySummary(statusResponse.data.data.summary);
        }
        await loadDailyLog();
        setMiscText('');
      } else {
        setError(response.data.error || 'Failed to log misc pickup');
      }
    } catch (error: any) {
      console.error('Misc pickup error:', error);
      setError(error.response?.data?.error || 'Failed to log misc pickup');
    } finally {
      setProcessing(false);
    }
  };

  // Handle equipment drop off
  const handleDropOff = async (equipment: LoadedEquipment) => {
    setProcessing(true);
    setError(null);

    try {
      // Always force a fresh GPS location for drop-offs to ensure correct job detection
      console.log('[TruckDriver] Forcing GPS refresh for drop-off...');
      const freshLocation = await forceRefreshLocation();

      const lat = freshLocation?.latitude || location?.latitude;
      const lng = freshLocation?.longitude || location?.longitude;

      // Always detect job from fresh GPS - don't use cached detectedJob
      let currentJobId: number | null = null;
      let currentJobName = 'Unknown';
      if (lat && lng) {
        const detected = detectJobFromLocation(lat, lng, jobs);
        if (detected) {
          setDetectedJob(detected);
          currentJobId = detected.job_id;
          currentJobName = detected.job_name;
        }
      }

      // If no job detected, use "Unknown" job as fallback (GPS coords will be logged)
      if (!currentJobId) {
        const unknownJob = jobs.find(j => j.job_name.toLowerCase() === 'unknown');
        if (unknownJob) {
          currentJobId = unknownJob.job_id;
          currentJobName = unknownJob.job_name;
          setDetectedJob(unknownJob);
        } else if (!isOnline) {
          // Offline with no unknown job - still allow dropoff
          currentJobName = 'Unknown (Offline)';
        } else {
          setError('Could not detect job site. Please select a job.');
          setShowJobPicker(true);
          setProcessing(false);
          return;
        }
      }

      // If offline, queue the action for later
      if (!isOnline) {
        const equipName = equipment.equipment_name || equipment.bradanna_equipment_number ||
          equipment.rental_description || 'Unknown';

        const pendingAction: PendingAction = {
          id: `unload_${equipment.movement_id}_${Date.now()}`,
          type: 'unload',
          equipment_id: equipment.equipment_id,
          equipment_name: equipName,
          job_id: currentJobId,
          job_name: currentJobName,
          lat,
          lng,
          is_rental: equipment.is_rental,
          rental_company: equipment.rental_company,
          rental_description: equipment.rental_description,
          timestamp: Date.now()
        };

        if (auth?.user?.employee_id) {
          addPendingAction(auth.user.employee_id, pendingAction);
          setPendingActions(getPendingActions(auth.user.employee_id));

          // Optimistically remove from loaded equipment
          setLoadedEquipment(prev => prev.filter(e => e.movement_id !== equipment.movement_id));
        }

        setProcessing(false);
        return;
      }

      const response = await api.post('/api/cdl/equipment/movement', {
        equipment_id: equipment.equipment_id,
        action: 'unload',
        job_id: currentJobId,
        lat,
        lng,
        is_rental: equipment.is_rental,
        rental_company: equipment.rental_company,
        rental_equipment_type: equipment.rental_equipment_type,
        rental_description: equipment.rental_description
      });

      if (response.data.success) {
        // Refresh loaded equipment and log
        const statusResponse = await api.get('/api/cdl/status');
        if (statusResponse.data.success) {
          setLoadedEquipment(statusResponse.data.data.loadedEquipment || []);
          setTodaySummary(statusResponse.data.data.summary);
        }
        await loadDailyLog();

        // Check if time entry was switched
        if (response.data.data.time_entry_switched) {
          // Refresh clock status
          const clockResponse = await api.get(`${API_CONFIG.ENDPOINTS.TIME}/current`);
          if (clockResponse.data.success && clockResponse.data.data?.job_id) {
            setActiveTimeEntry(clockResponse.data.data);
          }
        }
      } else {
        setError(response.data.error || 'Failed to log drop off');
      }
    } catch (error: any) {
      console.error('Drop off error:', error);
      setError(error.response?.data?.error || 'Failed to log drop off');
    } finally {
      setProcessing(false);
    }
  };

  // Handle dump load
  const handleDumpLoad = async (direction: 'delivered' | 'hauled_off') => {
    setProcessing(true);
    setError(null);

    try {
      // Always force a fresh GPS location for dump loads to ensure correct job detection
      console.log('[TruckDriver] Forcing GPS refresh for dump load...');
      const freshLocation = await forceRefreshLocation();

      const lat = freshLocation?.latitude;
      const lng = freshLocation?.longitude;

      // Always detect job from fresh GPS - don't use cached detectedJob
      let currentJobId = null;
      if (lat && lng) {
        const detected = detectJobFromLocation(lat, lng, jobs);
        if (detected) {
          setDetectedJob(detected);
          currentJobId = detected.job_id;
        }
      }

      // If no job detected, use "Unknown" job as fallback (GPS coords will be logged)
      if (!currentJobId) {
        const unknownJob = jobs.find(j => j.job_name.toLowerCase() === 'unknown');
        if (unknownJob) {
          currentJobId = unknownJob.job_id;
          setDetectedJob(unknownJob);
        } else {
          setError('Could not detect job site. Please select a job.');
          setShowJobPicker(true);
          setProcessing(false);
          return;
        }
      }

      const response = await api.post('/api/cdl/dump-load', {
        job_id: currentJobId,
        material_type: selectedMaterial,
        load_count: 1,
        lat,
        lng,
        notes: direction === 'hauled_off' ? 'Hauled off from site' : 'Delivered to site'
      });

      if (response.data.success) {
        // Refresh summary and log
        const statusResponse = await api.get('/api/cdl/status');
        if (statusResponse.data.success) {
          setTodaySummary(statusResponse.data.data.summary);
        }
        await loadDailyLog();
      } else {
        setError(response.data.error || 'Failed to log load');
      }
    } catch (error: any) {
      console.error('Dump load error:', error);
      setError(error.response?.data?.error || 'Failed to log load');
    } finally {
      setProcessing(false);
    }
  };

  // Calculate week start (Friday) for the current work week
  const getWeekStart = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();

    // Calculate days since last Friday
    // Friday = 5, Saturday = 6, Sunday = 0, Monday = 1, etc.
    let daysSinceLastFriday;
    if (dayOfWeek >= 5) {
      // Friday (5) or Saturday (6)
      daysSinceLastFriday = dayOfWeek - 5;
    } else {
      // Sunday (0) through Thursday (4)
      daysSinceLastFriday = dayOfWeek + 2;
    }

    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - daysSinceLastFriday);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  };

  // Load weekly data
  const loadWeeklyData = async () => {
    try {
      const weekStart = getWeekStart();
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const response = await api.get(
        `${API_CONFIG.ENDPOINTS.TIME}?employee_id=${auth?.user?.employee_id}&start_date=${weekStart.toISOString()}&end_date=${weekEnd.toISOString()}`
      );

      if (response.data.success && response.data.data) {
        // Build daily hours for Fri-Thu
        const dayNames = ['Fri', 'Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu'];
        const dailyHours: { day: string; date: string; hours: number }[] = [];
        let weekTotal = 0;

        // Helper to get local date string (MM/DD/YYYY format for comparison)
        const getLocalDateStr = (dateInput: Date | string) => {
          const d = new Date(dateInput);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };

        for (let i = 0; i < 7; i++) {
          const date = new Date(weekStart);
          date.setDate(weekStart.getDate() + i);
          const dateStr = getLocalDateStr(date);

          // Find entries for this date (comparing in local timezone)
          let dayHours = 0;
          response.data.data.forEach((entry: any) => {
            if (!entry.clock_out) return;

            // Convert clock_in to local date for comparison
            const entryDateStr = getLocalDateStr(entry.clock_in);
            if (entryDateStr === dateStr) {
              if (entry.hours_worked !== undefined && entry.hours_worked !== null) {
                dayHours += Math.abs(parseFloat(entry.hours_worked.toString()));
              }
            }
          });

          dailyHours.push({
            day: dayNames[i],
            date: `${date.getMonth() + 1}/${date.getDate()}`,
            hours: dayHours
          });
          weekTotal += dayHours;
        }

        setWeeklyData({ dailyHours, weekTotal });
      }
    } catch (error) {
      console.error('Error loading weekly data:', error);
    }
  };

  // Loading screen
  if (initializing || processing) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{
            color: 'white',
            animation: 'spin 1s linear infinite'
          }}
        />
        <div style={{
          color: 'white',
          fontSize: 18,
          marginTop: 20,
          fontWeight: '500'
        }}>
          {processing ? 'Processing...' : 'Loading...'}
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Offline Banner */}
      {(!isOnline || usingCachedData || pendingActions.length > 0) && (
        <div style={{
          background: !isOnline ? '#fee2e2' : pendingActions.length > 0 ? '#dbeafe' : '#fef3c7',
          borderBottom: `2px solid ${!isOnline ? '#ef4444' : pendingActions.length > 0 ? '#3b82f6' : '#f59e0b'}`,
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <WifiOff size={18} color={!isOnline ? '#dc2626' : pendingActions.length > 0 ? '#2563eb' : '#d97706'} />
          <span style={{ fontSize: '14px', fontWeight: '600', color: !isOnline ? '#991b1b' : pendingActions.length > 0 ? '#1e40af' : '#92400e' }}>
            {!isOnline
              ? 'No Internet Connection'
              : pendingActions.length > 0
                ? `${pendingActions.length} action${pendingActions.length > 1 ? 's' : ''} pending sync`
                : 'Showing Cached Data'
            }
          </span>
          {pendingActions.length > 0 && isOnline && (
            <span style={{ fontSize: '12px', color: '#3b82f6' }}>
              (syncing...)
            </span>
          )}
          {usingCachedData && isOnline && pendingActions.length === 0 && (
            <button
              onClick={() => window.location.reload()}
              style={{
                marginLeft: '8px',
                padding: '4px 12px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Refresh
            </button>
          )}
        </div>
      )}

      {/* Welcome Section - only show when NOT clocked in */}
      {!clockedIn && (
        <div className="welcome-section" style={{ marginBottom: '16px' }}>
          <h2 className="welcome-text" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Truck size={28} /> {auth?.user?.first_name}
          </h2>
          {clockedIn !== null && (
            <div className={`clock-badge ${clockedIn ? 'clocked-in' : 'not-clocked-in'}`}>
              {clockedIn ? 'CLOCKED IN' : 'NOT CLOCKED IN'}
            </div>
          )}
        </div>
      )}

      {/* Current Location */}
      {clockedIn && (
        <div style={{
          background: 'white',
          margin: '16px',
          padding: '16px 20px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
        }}>
          <MapPin size={24} color="#3b82f6" />
          <span style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937' }}>
            {detectedJob?.job_name || activeTimeEntry?.job_name || 'Detecting...'}
          </span>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #fecaca',
          borderRadius: '12px',
          padding: '12px 16px',
          margin: '0 16px 16px',
          color: '#dc2626',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc2626',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Start Day Button (when not clocked in) */}
      {!clockedIn && (
        <div style={{ padding: '0 16px' }}>
          <button
            onClick={handleStartDay}
            className="action-button"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              marginBottom: '16px'
            }}
          >
            <span className="button-icon">🟢</span>
            <span className="button-label">Start Day</span>
            <span className="button-arrow">→</span>
          </button>
        </div>
      )}

      {/* Mode Toggle */}
      {clockedIn && (
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '0 16px',
          marginBottom: '16px'
        }}>
          <button
            onClick={() => setMode('equipment')}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: mode === 'equipment' ? '#3b82f6' : '#e5e7eb',
              color: mode === 'equipment' ? 'white' : '#6b7280',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <Package size={18} />
            Equipment Hauler
          </button>
          <button
            onClick={() => setMode('dump')}
            style={{
              flex: 1,
              padding: '12px',
              borderRadius: '10px',
              border: 'none',
              background: mode === 'dump' ? '#f59e0b' : '#e5e7eb',
              color: mode === 'dump' ? 'white' : '#6b7280',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <Truck size={18} />
            Dump Truck
          </button>
        </div>
      )}

      {/* Equipment Hauler Mode */}
      {clockedIn && mode === 'equipment' && (
        <>
          {/* Equipment Search - FIRST */}
          <div style={{
            background: 'white',
            margin: '0 16px 16px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: isIPad ? '18px 20px' : '14px 16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <Search size={isIPad ? 32 : 24} color="#3b82f6" />
              <span style={{ fontSize: fontSize(18), fontWeight: '600', color: '#1f2937' }}>
                Add Equipment
              </span>
            </div>

            <div style={{ padding: isIPad ? '18px 20px' : '14px 16px' }}>
              <input
                type="text"
                inputMode="numeric"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type equipment # or name..."
                style={{
                  width: '100%',
                  padding: isIPad ? '18px 20px' : '14px 16px',
                  fontSize: fontSize(18),
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  outline: 'none'
                }}
              />
            </div>

            {searching && (
              <div style={{ padding: isIPad ? '18px' : '14px', textAlign: 'center', color: '#6b7280', fontSize: fontSize(16) }}>
                Searching...
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{ maxHeight: isIPad ? '350px' : '250px', overflowY: 'auto' }}>
                {searchResults.map((equip) => {
                  const isOnTrailer = loadedEquipment.some(loaded => loaded.equipment_id === equip.equipment_id);
                  return (
                    <div
                      key={equip.equipment_id}
                      style={{
                        padding: isIPad ? '18px 20px' : '14px 16px',
                        borderTop: '1px solid #f0f2f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: isOnTrailer ? '#fef3c7' : 'transparent'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: fontSize(17), fontWeight: '600', color: '#1f2937' }}>
                          {equip.bradanna_equipment_number} {equip.equipment_name || equip.equipment_type}
                        </div>
                        <div style={{ fontSize: fontSize(14), color: isOnTrailer ? '#d97706' : '#9ca3af', marginTop: '4px' }}>
                          {isOnTrailer ? 'Already on your trailer' : (equip.job_name ? `At: ${equip.job_name}` : 'At: Shop')}
                        </div>
                      </div>
                      {isOnTrailer ? (
                        <div style={{
                          background: '#fbbf24',
                          color: '#92400e',
                          borderRadius: '8px',
                          padding: isIPad ? '14px 20px' : '12px 16px',
                          fontSize: fontSize(14),
                          fontWeight: '600'
                        }}>
                          ON TRAILER
                        </div>
                      ) : (
                        <button
                          onClick={() => handlePickUp(equip)}
                          disabled={processing}
                          style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: isIPad ? '14px 24px' : '12px 20px',
                            fontSize: fontSize(16),
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          PICK UP
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add Rental & Misc buttons - below search results */}
            <div style={{ padding: isIPad ? '18px 20px' : '14px 16px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setShowRentalModal(true)}
                style={{
                  width: '100%',
                  padding: isIPad ? '16px 20px' : '12px 16px',
                  fontSize: fontSize(16),
                  fontWeight: '600',
                  background: '#8b5cf6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                + Add Rental Equipment
              </button>
              <button
                onClick={() => setShowMiscModal(true)}
                style={{
                  width: '100%',
                  marginTop: '8px',
                  padding: isIPad ? '16px 20px' : '12px 16px',
                  fontSize: fontSize(16),
                  fontWeight: '600',
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                + Add Misc (Parts, etc.)
              </button>
            </div>
          </div>

          {/* On My Trailer Section - SECOND */}
          <div style={{
            background: '#fef3c7',
            margin: '0 16px 16px',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden',
            border: '2px solid #f59e0b'
          }}>
            <div style={{
              padding: isIPad ? '18px 20px' : '14px 16px',
              borderBottom: '2px solid #f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: '#fde68a'
            }}>
              <Truck size={isIPad ? 32 : 24} color="#b45309" />
              <span style={{ fontSize: fontSize(18), fontWeight: '600', color: '#92400e' }}>
                On My Trailer ({loadedEquipment.length})
              </span>
            </div>

            {loadedEquipment.length === 0 ? (
              <div style={{
                padding: isIPad ? '32px' : '24px',
                textAlign: 'center',
                color: '#92400e',
                fontSize: fontSize(16)
              }}>
                No equipment loaded
              </div>
            ) : (
              <div>
                {loadedEquipment.map((equip) => {
                  // Determine item type and display name
                  const isMisc = equip.is_rental && equip.rental_company === 'MISC';
                  const isRental = equip.is_rental && equip.rental_company !== 'MISC';

                  let equipName: string;
                  let itemLabel: string;

                  if (isMisc) {
                    equipName = equip.rental_description || 'Misc Item';
                    itemLabel = 'Parts/Materials';
                  } else if (isRental) {
                    equipName = equip.rental_description || equip.rental_equipment_type || 'Rental';
                    itemLabel = `Rental - ${equip.rental_company || 'Unknown'}`;
                  } else {
                    equipName = `${equip.bradanna_equipment_number || ''} ${equip.equipment_name || equip.equipment_type || ''}`.trim();
                    itemLabel = '';
                  }

                  return (
                    <div
                      key={equip.movement_id}
                      style={{
                        padding: isIPad ? '18px 20px' : '14px 16px',
                        borderBottom: '1px solid #fcd34d',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: isMisc ? '#fef9c3' : 'transparent'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: fontSize(17), fontWeight: '600', color: '#78350f' }}>
                          {equipName}
                        </div>
                        {itemLabel && (
                          <div style={{ fontSize: fontSize(13), color: isMisc ? '#ca8a04' : '#b45309', marginTop: '2px', fontStyle: 'italic' }}>
                            {itemLabel}
                          </div>
                        )}
                        <div style={{ fontSize: fontSize(14), color: '#92400e', marginTop: '4px' }}>
                          Loaded at {formatTime(equip.loaded_at)}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDropOff(equip)}
                        disabled={processing}
                        style={{
                          background: isMisc ? '#eab308' : '#f59e0b',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          padding: isIPad ? '14px 24px' : '12px 20px',
                          fontSize: fontSize(16),
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        {isMisc ? 'DELIVERED' : 'DROP OFF'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Dump Truck Mode */}
      {clockedIn && mode === 'dump' && (
        <div style={{
          background: 'white',
          margin: '0 16px 16px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '16px'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
              Material Type:
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {['fill', 'rock', 'sand', 'base', 'demo', 'asphalt', 'millings', 'vegetation'].map((material) => (
                <button
                  key={material}
                  onClick={() => setSelectedMaterial(material)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: selectedMaterial === material ? '#3b82f6' : '#f0f2f5',
                    color: selectedMaterial === material ? 'white' : '#4b5563',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {material}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => handleDumpLoad('delivered')}
            disabled={processing}
            className="action-button"
            style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              marginBottom: '12px',
              height: '70px'
            }}
          >
            <span className="button-icon">📥</span>
            <span className="button-label">
              <div>Delivered to Site</div>
              <div style={{ fontSize: '12px', opacity: 0.8, fontWeight: '400' }}>
                Bills to: {detectedJob?.job_name || 'Current location'}
              </div>
            </span>
            <span className="button-arrow">→</span>
          </button>

          <button
            onClick={() => handleDumpLoad('hauled_off')}
            disabled={processing}
            className="action-button"
            style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              height: '70px'
            }}
          >
            <span className="button-icon">📤</span>
            <span className="button-label">
              <div>Hauled Off from Site</div>
              <div style={{ fontSize: '12px', opacity: 0.8, fontWeight: '400' }}>
                Bills to: {detectedJob?.job_name || 'Current location'} (origin)
              </div>
            </span>
            <span className="button-arrow">→</span>
          </button>

          {todaySummary && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#f0f2f5',
              borderRadius: '8px',
              textAlign: 'center',
              fontSize: '14px',
              color: '#4b5563'
            }}>
              Today's Loads: <strong>{todaySummary.dump_loads_today || 0}</strong>
            </div>
          )}
        </div>
      )}

      {/* End Day Button (when clocked in) */}
      {clockedIn && (
        <div style={{ padding: '16px 16px 0' }}>
          <button
            onClick={() => setShowEndDayConfirm(true)}
            className="action-button"
            style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            }}
          >
            <span className="button-icon">🔴</span>
            <span className="button-label">End Day</span>
            <span className="button-arrow">→</span>
          </button>
        </div>
      )}


      {/* Spacer for fixed bottom nav */}
      <div style={{ height: '80px' }} />

      {/* Custom Bottom Navigation for Truck Driver */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        padding: '8px 0 12px',
        zIndex: 1000,
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#3b82f6'
          }}
        >
          <Home size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Home</span>
        </button>

        <button
          onClick={() => setShowDailyLogModal(true)}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          <Clock size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Full Log</span>
        </button>

        <button
          onClick={async () => {
            await loadWeeklyData();
            setShowWeeklyModal(true);
          }}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          <Calendar size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Weekly Hrs</span>
        </button>

        <button
          onClick={() => navigate('/equipment')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#ef4444'
          }}
        >
          <AlertTriangle size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Report Issue</span>
        </button>

        <button
          onClick={() => navigate('/cdl-jobs')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#10b981'
          }}
        >
          <Briefcase size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Jobs</span>
        </button>

        <button
          onClick={() => navigate('/my-time')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#6366f1'
          }}
        >
          <Clock size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>My Time</span>
        </button>
      </nav>

      {/* Job Picker Modal */}
      {showJobPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-end',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            width: '100%',
            maxHeight: '80vh',
            borderRadius: '24px 24px 0 0',
            padding: '24px',
            overflowY: 'auto'
          }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Select Job
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              No job detected at your location. Please select a job:
            </p>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginBottom: '16px'
            }}>
              {jobs.map(job => (
                <button
                  key={job.job_id}
                  onClick={() => handleClockIn(job)}
                  style={{
                    background: '#3b82f6',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '16px',
                    color: 'white',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  <div>{job.job_name}</div>
                  {job.job_number && (
                    <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
                      #{job.job_number}
                    </div>
                  )}
                </button>
              ))}
            </div>

            <button
              onClick={() => setShowJobPicker(false)}
              style={{
                width: '100%',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                color: '#6b7280',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* End Day Confirmation Modal */}
      {showEndDayConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '22px',
              fontWeight: '700',
              color: '#1f2937'
            }}>
              End Day?
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              Are you sure you want to clock out for the day?
              <br /><br />
              You will be clocked out and your shift will end.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleEndDay}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px',
                  color: 'white',
                  fontSize: '17px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Yes, End My Day
              </button>
              <button
                onClick={() => setShowEndDayConfirm(false)}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px',
                  color: '#6b7280',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clock Out Result Modal - shows location and distance from shop */}
      {clockOutResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {clockOutResult.distanceFromShop ? '📍' : '✅'}
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              fontSize: '22px',
              fontWeight: '700',
              color: '#1f2937'
            }}>
              Day Ended
            </h3>
            <p style={{
              margin: '0 0 8px 0',
              fontSize: '16px',
              color: '#374151',
              fontWeight: '600'
            }}>
              Clocked out at: {clockOutResult.locationName}
            </p>
            {clockOutResult.distanceFromShop !== undefined && (
              <p style={{
                margin: '0 0 24px 0',
                fontSize: '15px',
                color: '#6b7280',
                lineHeight: '1.5'
              }}>
                Distance from shop: <strong>{clockOutResult.distanceFromShop.toFixed(1)} miles</strong>
              </p>
            )}
            {!clockOutResult.distanceFromShop && (
              <div style={{ marginBottom: '24px' }} />
            )}

            <button
              onClick={() => setClockOutResult(null)}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                color: 'white',
                fontSize: '17px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Daily Log Modal */}
      {showDailyLogModal && (() => {
        // Filter log entries to current day only
        const now = new Date();
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        const todayLog = dailyLog.filter(entry => {
          const entryDate = new Date(entry.clock_in);
          const entryLocalStr = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`;
          return entryLocalStr === todayStr;
        });

        // Sort by clock_in time (oldest first)
        todayLog.sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime());

        // Calculate first clock in time
        const firstEntry = todayLog[0];
        const clockInTime = firstEntry ? formatTime(firstEntry.clock_in) : (activeTimeEntry?.clock_in ? formatTime(activeTimeEntry.clock_in) : null);

        // Calculate TOTAL hours worked today
        let hoursWorked = todayLog.reduce((sum, entry) => {
          if (entry.hours_worked) return sum + entry.hours_worked;
          // Open entry (no clock_out) - calculate elapsed time from clock_in to now
          if (!entry.clock_out && entry.clock_in) {
            const clockInDate = new Date(entry.clock_in);
            return sum + (now.getTime() - clockInDate.getTime()) / (1000 * 60 * 60);
          }
          return sum;
        }, 0);

        // Add active entry's elapsed time if it's not in the log at all
        if (activeTimeEntry?.clock_in && !todayLog.some(e => e.entry_id === activeTimeEntry.entry_id)) {
          const clockInDate = new Date(activeTimeEntry.clock_in);
          hoursWorked += (now.getTime() - clockInDate.getTime()) / (1000 * 60 * 60);
        }

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-end',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              width: '100%',
              maxHeight: '80vh',
              borderRadius: '24px 24px 0 0',
              padding: '24px',
              overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '16px'
              }}>
                <h3 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  Today's Log
                </h3>
                <button
                  onClick={() => setShowDailyLogModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>

              {/* Clock In Time and Hours Summary */}
              {(clockInTime || hoursWorked > 0) && (
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginBottom: '4px' }}>
                      Started
                    </div>
                    <div style={{ color: 'white', fontSize: '22px', fontWeight: '700' }}>
                      {clockInTime || '--'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginBottom: '4px' }}>
                      Total Hours
                    </div>
                    <div style={{ color: 'white', fontSize: '22px', fontWeight: '700' }}>
                      {hoursWorked > 0 ? hoursWorked.toFixed(2) : '0.00'} hrs
                    </div>
                  </div>
                </div>
              )}

              {todayLog.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>
                  No activity logged today.
                </div>
              ) : (
                <div>
                  {todayLog.map((entry, idx) => {
                    // Build activity summary
                    const activities: string[] = [];
                    if (entry.picked_up && entry.picked_up.length > 0) {
                      activities.push(`Picked up: ${entry.picked_up.join(', ')}`);
                    }
                    if (entry.dropped_off && entry.dropped_off.length > 0) {
                      activities.push(`Dropped off: ${entry.dropped_off.join(', ')}`);
                    }
                    if (entry.dump_loads && entry.dump_loads > 0) {
                      const materials = entry.dump_materials?.join(', ') || 'material';
                      activities.push(`${entry.dump_loads} load${entry.dump_loads > 1 ? 's' : ''} of ${materials}`);
                    }

                    return (
                      <div
                        key={entry.entry_id || idx}
                        style={{
                          padding: '14px',
                          marginBottom: '10px',
                          background: '#f8fafc',
                          borderRadius: '12px',
                          borderLeft: `4px solid ${entry.clock_out ? '#10b981' : '#f59e0b'}`
                        }}
                      >
                        {/* Job Name and Time Range */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                            {entry.job_name || 'Unknown'}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'right' }}>
                            {formatTime(entry.clock_in)} - {entry.clock_out ? formatTime(entry.clock_out) : 'Now'}
                          </div>
                        </div>

                        {/* Hours */}
                        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: activities.length > 0 ? '8px' : '0' }}>
                          {entry.hours_worked ? `${entry.hours_worked.toFixed(2)} hrs` : (entry.clock_out ? '0.00 hrs' : 'In progress')}
                        </div>

                        {/* Activities */}
                        {activities.length > 0 && (
                          <div style={{ fontSize: '14px', color: '#374151' }}>
                            {activities.map((activity, aIdx) => (
                              <div key={aIdx} style={{ marginTop: '4px' }}>• {activity}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Officer Button */}
              <button
                onClick={() => {
                  setShowDailyLogModal(false);
                  setShowOfficerModal(true);
                }}
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Shield size={20} />
                Show to Officer
              </button>

              <button
                onClick={() => setShowDailyLogModal(false)}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '16px',
                  color: '#6b7280',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
              >
                Close
              </button>
            </div>
          </div>
        );
      })()}

      {/* Weekly Hours Modal */}
      {showWeeklyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-end',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            width: '100%',
            maxHeight: '80vh',
            borderRadius: '24px 24px 0 0',
            padding: '24px',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937'
              }}>
                Weekly Hours
              </h3>
              <button
                onClick={() => setShowWeeklyModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                ×
              </button>
            </div>

            {weeklyData ? (
              <>
                <div style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  borderRadius: '16px',
                  padding: '20px',
                  textAlign: 'center',
                  marginBottom: '20px'
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginBottom: '4px' }}>
                    Week Total
                  </div>
                  <div style={{ color: 'white', fontSize: '36px', fontWeight: '700' }}>
                    {Number(weeklyData.weekTotal || 0).toFixed(1)} hrs
                  </div>
                </div>

                <div>
                  {weeklyData.dailyHours?.map((dayData: any, idx: number) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f2f5'
                      }}
                    >
                      <div>
                        <span style={{ fontSize: '15px', color: '#1f2937', fontWeight: '500' }}>
                          {dayData.day}
                        </span>
                        <span style={{ fontSize: '13px', color: '#9ca3af', marginLeft: '8px' }}>
                          {dayData.date}
                        </span>
                      </div>
                      <span style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: dayData.hours > 0 ? '#1f2937' : '#d1d5db'
                      }}>
                        {Number(dayData.hours).toFixed(1)} hrs
                      </span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>
                Loading weekly data...
              </div>
            )}

            <button
              onClick={() => setShowWeeklyModal(false)}
              style={{
                width: '100%',
                background: '#3b82f6',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '20px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Rental Equipment Modal */}
      {showRentalModal && (() => {
        const rentalCompanies = [
          'Kelly Tractor',
          'United Rentals',
          'Synergy',
          'Sunbelt',
          'Nortrax',
          'Alta',
          'Other'
        ];

        const equipmentTypes = [
          'Excavator',
          'Mini Excavator',
          'Skid Steer',
          'Backhoe',
          'Dozer',
          'Loader',
          'Grader',
          'Paver',
          'Roller',
          'Dump Truck',
          'Flatbed',
          'Crane',
          'Forklift',
          'Scissor Lift',
          'Boom Lift',
          'Generator',
          'Compressor',
          'Light Tower',
          'Trailer',
          'Other'
        ];

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: isIPad ? '28px' : '24px',
              maxWidth: isIPad ? '500px' : '400px',
              width: '100%'
            }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: fontSize(20),
                fontWeight: '600',
                color: '#1f2937',
                textAlign: 'center'
              }}>
                Add Rental Equipment
              </h3>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: fontSize(14),
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px'
                }}>
                  Rental Company *
                </label>
                <CustomSelect
                  value={rentalCompany}
                  onChange={(value) => setRentalCompany(value)}
                  placeholder="Select rental company..."
                  options={rentalCompanies.map(company => ({
                    value: company,
                    label: company
                  }))}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: fontSize(14),
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px'
                }}>
                  Equipment Type *
                </label>
                <CustomSelect
                  value={rentalDescription}
                  onChange={(value) => setRentalDescription(value)}
                  placeholder="Select equipment type..."
                  options={equipmentTypes.map(type => ({
                    value: type,
                    label: type
                  }))}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  onClick={handleRentalPickUp}
                  disabled={!rentalDescription || !rentalCompany}
                  style={{
                    width: '100%',
                    background: (rentalDescription && rentalCompany) ? '#10b981' : '#9ca3af',
                    border: 'none',
                    borderRadius: '12px',
                    padding: isIPad ? '18px' : '16px',
                    color: 'white',
                    fontSize: fontSize(17),
                    fontWeight: '700',
                    cursor: (rentalDescription && rentalCompany) ? 'pointer' : 'not-allowed'
                  }}
                >
                  PICK UP RENTAL
                </button>
                <button
                  onClick={() => {
                    setShowRentalModal(false);
                    setRentalCompany('');
                    setRentalDescription('');
                  }}
                  style={{
                    width: '100%',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '12px',
                    padding: isIPad ? '18px' : '16px',
                    color: '#6b7280',
                    fontSize: fontSize(16),
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Misc Activity Modal */}
      {showMiscModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          paddingTop: '10vh',
          paddingBottom: '40vh',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch' as const
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: isIPad ? '28px' : '24px',
            maxWidth: isIPad ? '500px' : '400px',
            width: '100%',
            flexShrink: 0
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: fontSize(20),
              fontWeight: '600',
              color: '#1f2937',
              textAlign: 'center'
            }}>
              Add Misc Activity
            </h3>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: fontSize(14),
                fontWeight: '600',
                color: '#6b7280',
                marginBottom: '8px'
              }}>
                Description *
              </label>
              <input
                type="text"
                value={miscText}
                onChange={(e) => setMiscText(e.target.value)}
                placeholder="e.g., Parts pickup, Fuel stop..."
                style={{
                  width: '100%',
                  padding: isIPad ? '18px 20px' : '14px 16px',
                  fontSize: fontSize(16),
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <button
                onClick={handleMiscSubmit}
                disabled={!miscText.trim()}
                style={{
                  width: '100%',
                  background: miscText.trim() ? '#f59e0b' : '#9ca3af',
                  border: 'none',
                  borderRadius: '12px',
                  padding: isIPad ? '18px' : '16px',
                  color: 'white',
                  fontSize: fontSize(17),
                  fontWeight: '700',
                  cursor: miscText.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                ADD TO LOG
              </button>
              <button
                onClick={() => {
                  setShowMiscModal(false);
                  setMiscText('');
                }}
                style={{
                  width: '100%',
                  background: '#f3f4f6',
                  border: 'none',
                  borderRadius: '12px',
                  padding: isIPad ? '18px' : '16px',
                  color: '#6b7280',
                  fontSize: fontSize(16),
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Officer Compliance Modal - Show to Officer */}
      {showOfficerModal && (() => {
        const now = new Date();
        const todayDate = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        const currentTime = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

        // Calculate hours worked today
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        // Filter to today's entries
        const todayEntries = dailyLog.filter(entry => {
          const entryDate = new Date(entry.clock_in);
          const entryLocalStr = `${entryDate.getFullYear()}-${String(entryDate.getMonth() + 1).padStart(2, '0')}-${String(entryDate.getDate()).padStart(2, '0')}`;
          return entryLocalStr === todayStr;
        });

        // Sum hours from entries, calculating elapsed time for open entries
        let hoursWorked = todayEntries.reduce((sum, entry) => {
          if (entry.hours_worked) return sum + entry.hours_worked;
          // Open entry (no clock_out) - calculate elapsed time from clock_in to now
          if (!entry.clock_out && entry.clock_in) {
            const clockInDate = new Date(entry.clock_in);
            return sum + (now.getTime() - clockInDate.getTime()) / (1000 * 60 * 60);
          }
          return sum;
        }, 0);

        // Add active entry's elapsed time if it's not in the log at all
        if (activeTimeEntry?.clock_in && !todayEntries.some(e => e.entry_id === activeTimeEntry.entry_id)) {
          const clockInDate = new Date(activeTimeEntry.clock_in);
          hoursWorked += (now.getTime() - clockInDate.getTime()) / (1000 * 60 * 60);
        }

        // Get first clock in time
        const firstEntry = todayEntries.sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime())[0];
        const clockInTime = firstEntry ? formatTime(firstEntry.clock_in) : (activeTimeEntry?.clock_in ? formatTime(activeTimeEntry.clock_in) : null);

        // Calculate 14-hour window remaining
        const hoursRemaining = Math.max(0, 14 - hoursWorked);

        return (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#0f172a',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 9999,
            overflow: 'auto'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '8px' }}>
                <Shield size={32} color="white" />
                <span style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>DRIVER TIME RECORD</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>
                49 CFR 395.1(e)(1) Short-Haul Exemption
              </div>
            </div>

            {/* Company Info */}
            <div style={{
              background: '#1e293b',
              padding: '16px 20px',
              borderBottom: '1px solid #334155'
            }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>CARRIER</div>
              <div style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>BRADANNA INC.</div>
              <div style={{ color: '#fbbf24', fontSize: '16px', fontWeight: '600', marginTop: '4px' }}>
                USDOT #811837
              </div>
              <div style={{ color: '#94a3b8', fontSize: '14px', marginTop: '4px' }}>
                Work Reporting Location: 4949 Tamiami Trail N, Suite 208, Naples, FL 34103
              </div>
            </div>

            {/* Driver Info */}
            <div style={{
              background: '#1e293b',
              padding: '16px 20px',
              borderBottom: '1px solid #334155'
            }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>DRIVER</div>
              <div style={{ color: 'white', fontSize: '20px', fontWeight: '700' }}>
                {auth?.user?.first_name} {auth?.user?.last_name}
              </div>
            </div>

            {/* Date/Time Info */}
            <div style={{
              background: '#1e293b',
              padding: '16px 20px',
              borderBottom: '1px solid #334155'
            }}>
              <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>DATE</div>
              <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>{todayDate}</div>
              <div style={{ color: '#94a3b8', fontSize: '14px', marginTop: '8px' }}>
                Current Time: <span style={{ color: 'white', fontWeight: '600' }}>{currentTime}</span>
              </div>
            </div>

            {/* Time Record */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1px',
              background: '#334155'
            }}>
              <div style={{ background: '#1e293b', padding: '16px 20px', textAlign: 'center' }}>
                <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px' }}>START TIME</div>
                <div style={{ color: '#10b981', fontSize: '28px', fontWeight: '700' }}>
                  {clockInTime || '--:--'}
                </div>
              </div>
              <div style={{ background: '#1e293b', padding: '16px 20px', textAlign: 'center' }}>
                <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px' }}>HOURS WORKED</div>
                <div style={{ color: '#3b82f6', fontSize: '28px', fontWeight: '700' }}>
                  {hoursWorked.toFixed(1)} hrs
                </div>
              </div>
            </div>

            {/* 14-Hour Window */}
            <div style={{
              background: hoursRemaining > 2 ? '#065f46' : hoursRemaining > 0 ? '#92400e' : '#991b1b',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginBottom: '8px' }}>
                14-HOUR DRIVING WINDOW REMAINING
              </div>
              <div style={{ color: 'white', fontSize: '36px', fontWeight: '700' }}>
                {hoursRemaining.toFixed(1)} hours
              </div>
            </div>

            {/* Status Badge */}
            <div style={{
              padding: '20px',
              textAlign: 'center'
            }}>
              {clockedIn ? (
                <div style={{
                  display: 'inline-block',
                  background: '#10b981',
                  color: 'white',
                  padding: '12px 32px',
                  borderRadius: '50px',
                  fontSize: '18px',
                  fontWeight: '700'
                }}>
                  ON DUTY - DRIVING
                </div>
              ) : (
                <div style={{
                  display: 'inline-block',
                  background: '#6b7280',
                  color: 'white',
                  padding: '12px 32px',
                  borderRadius: '50px',
                  fontSize: '18px',
                  fontWeight: '700'
                }}>
                  OFF DUTY
                </div>
              )}
            </div>

            {/* Exemption Info */}
            <div style={{
              background: '#1e293b',
              margin: '0 16px',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ color: '#fbbf24', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                SHORT-HAUL EXEMPTION QUALIFICATIONS
              </div>
              <ul style={{ color: '#94a3b8', fontSize: '13px', margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
                <li>Operating within 150 air-mile radius of work reporting location</li>
                <li>Return to work reporting location within 14 hours</li>
                <li>Time records maintained by carrier</li>
                <li>Released from duty within 14 consecutive hours</li>
              </ul>
            </div>

            {/* Close Button */}
            <div style={{ padding: '16px', marginTop: 'auto' }}>
              <button
                onClick={() => setShowOfficerModal(false)}
                style={{
                  width: '100%',
                  background: '#3b82f6',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '18px',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
